import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  Stack,
} from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TextFieldFilter, TextFieldFilterProps } from '@cnx-saas/common-fe';
import { UserFilters, UserStatus, UserType } from '@cnx-saas/common';
import { getUserTypes } from '../type';

export interface FiltersProps {
  firstNameProps: TextFieldFilterProps;
  lastNameProps: TextFieldFilterProps;
  emailProps: TextFieldFilterProps;
  userTypeProps: SelectProps<UserType>;
  statusProps: SelectProps<UserStatus>;
}

export const Filters = ({
  firstNameProps,
  lastNameProps,
  emailProps,
  userTypeProps,
  statusProps,
}: FiltersProps) => {
  const { t } = useTranslation();

  const filterUserType = useMemo(() => getUserTypes(), []);

  return (
    <Stack flexDirection="row" gap={2} mt={3} gridArea="filters">
      <TextFieldFilter {...firstNameProps} />
      <TextFieldFilter {...lastNameProps} />
      <TextFieldFilter {...emailProps} />
      <FormControl sx={{ width: 220 }}>
        <InputLabel id="usertype-select-label" size="small">
          {t('userManagement.label.userType', {
            defaultValue: 'Account Type',
          })}
        </InputLabel>
        <Select labelId="usertype-select-label" {...userTypeProps} size="small">
          {Object.values(filterUserType).map((option) => (
            <MenuItem key={option} value={option}>
              {t(`userManagement.label.${option}`, {
                defaultValue: option,
              })}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl sx={{ width: 220 }}>
        <InputLabel id="usertype-select-label" size="small">
          {t('userManagement.lebel.userStatus', {
            defaultValue: 'Status',
          })}
        </InputLabel>
        <Select labelId="userstatus-select-label" {...statusProps} size="small">
          {Object.values(UserStatus).map((option) => (
            <MenuItem key={option} value={option}>
              {t(`userManagement.label.${option}`, {
                defaultValue: option,
              })}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
};

export function useFilters() {
  const { t } = useTranslation();

  const [firstName, setFirstName] = React.useState('');
  const firstNameProps = React.useMemo<TextFieldFilterProps>(
    () => ({
      value: firstName,
      placeholder: t('userManagement.label.firstName', {
        defaultValue: 'First Name',
      }),
      onChange: setFirstName,
    }),
    [t, firstName],
  );

  const [lastName, setLastName] = React.useState('');
  const lastNameProps = React.useMemo<TextFieldFilterProps>(
    () => ({
      value: lastName,
      placeholder: t('userManagement.label.lastName', {
        defaultValue: 'Last Name',
      }),
      onChange: setLastName,
    }),
    [t, lastName],
  );
  const [email, setEmail] = React.useState('');
  const emailProps = React.useMemo<TextFieldFilterProps>(
    () => ({
      value: email,
      placeholder: t('userManagement.label.email', {
        defaultValue: 'email',
      }),
      onChange: setEmail,
    }),
    [t, email],
  );

  const [userType, setUserType] = React.useState('');
  const userTypeProps = React.useMemo<SelectProps<UserType>>(
    () => ({
      value: userType as UserType,
      onChange(event) {
        setUserType(event.target.value);
      },
    }),
    [userType],
  );
  const [userStatus, setUserStatus] = React.useState('');
  const statusProps = React.useMemo<SelectProps<UserStatus>>(
    () => ({
      value: userStatus as UserStatus,
      onChange(event) {
        setUserStatus(event.target.value);
      },
    }),
    [userStatus],
  );

  const userFilters = useMemo(() => {
    const filter: UserFilters = {};

    if (firstName) {
      filter.firstName = firstName;
    }

    if (lastName) {
      filter.lastName = lastName;
    }

    if (email) {
      filter.email = email;
    }

    if (userType) {
      filter.userType = userType as UserType;
    }

    if (userStatus) {
      filter.status = userStatus as UserStatus;
    }

    return filter;
  }, [email, firstName, lastName, userStatus, userType]);

  function clearAllFilters() {
    setFirstName('');
    setLastName('');
    setEmail('');
    setUserType('');
    setUserStatus('');
  }

  return {
    userFilters,
    firstNameProps,
    lastNameProps,
    emailProps,
    userTypeProps,
    statusProps,
    clearAllFilters,
    filtersActive: Object.keys(userFilters).length > 0,
  };
}
